import React, { useEffect, useState } from "react";
import API from "../../../util/API";
import { Badge, Button, Card, Field, Input } from "./mailUI";

const EmailSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [testTo, setTestTo] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [testStatus, setTestStatus] = useState({ type: "", text: "" });

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/mail/config");
      setConfig(res.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load email settings"
      );
      setConfig(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const onSendTest = async () => {
    setTestLoading(true);
    setTestStatus({ type: "", text: "" });
    try {
      await API.post("/mail/smtp/test", { to: testTo });
      setTestStatus({ type: "success", text: "SMTP test email sent." });
    } catch (err) {
      setTestStatus({
        type: "error",
        text:
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "SMTP test failed",
      });
    } finally {
      setTestLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card
        title="SMTP Settings"
        subtitle="Read-only view of server SMTP configuration."
        right={
          config?.hasAuth ? (
            <Badge tone="success">SMTP Auth Ready</Badge>
          ) : (
            <Badge tone="warning">SMTP Auth Missing</Badge>
          )
        }
      >
        {loading ? (
          <p className="text-sm text-slate-500">Loading settings...</p>
        ) : error ? (
          <p className="text-sm text-rose-600">{error}</p>
        ) : config ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="SMTP Host">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.host || "—"}
              </div>
            </Field>
            <Field label="SMTP Port">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.port || "—"}
              </div>
            </Field>
            <Field label="Secure">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.secure ? "Yes" : "No"}
              </div>
            </Field>
            <Field label="Require TLS">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.requireTLS ? "Yes" : "No"}
              </div>
            </Field>
            <Field label="From Name">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.from?.name || "—"}
              </div>
            </Field>
            <Field label="From Email">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.from?.email || "—"}
              </div>
            </Field>
            <Field label="DKIM Enabled">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.dkim?.enabled ? "Yes" : "No"}
              </div>
            </Field>
            <Field label="DKIM Domain">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.dkim?.domain || "—"}
              </div>
            </Field>
            <Field label="DKIM Selector">
              <div className="rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800">
                {config.dkim?.selector || "—"}
              </div>
            </Field>
          </div>
        ) : (
          <p className="text-sm text-slate-500">No configuration available.</p>
        )}
      </Card>

      <Card
        title="SMTP Test"
        subtitle="Send a basic test email to verify connectivity."
      >
        <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end">
          <Field label="Send Test To">
            <Input
              value={testTo}
              onChange={(event) => setTestTo(event.target.value)}
              placeholder="admin@company.com"
              type="email"
            />
          </Field>
          <Button onClick={onSendTest} disabled={testLoading || !testTo}>
            {testLoading ? "Sending..." : "Send Test"}
          </Button>
        </div>
        {testStatus.text ? (
          <p
            className={`mt-3 text-sm ${
              testStatus.type === "success" ? "text-emerald-600" : "text-rose-600"
            }`}
          >
            {testStatus.text}
          </p>
        ) : null}
      </Card>
    </div>
  );
};

export default EmailSettings;
